<template>
  <div class="chat-history-shell">
    <div
      ref="history"
      class="chat-history"
      @scroll="handleScroll"
    >
      <v-row
        no-gutters
        class="messages-container"
      >
        <v-col
          cols="12"
          sm="12"
          md="12"
          class="messages-col"
        >
          <transition-group
            name="message-in"
            tag="div"
            class="messages-list"
          >
            <chat-message
              v-for="(message, index) in data"
              :key="messageKey(message, index)"
              :message="message"
              :animate="isStreamingMessage(message, index)"
              :show-edit-button="index === lastUserMessageIndex"
              :is-editing="index === editingMessageIndex"
              :streaming-active="streamingActive"
              :is-last-message="index === lastAiMessageIndex && index === data.length - 1"
              @content-rendered="handleContentRendered"
              @rerun-prompt="handleRerunMessage(index)"
              @edit-prompt="handleEditPrompt(index)"
              @cancel-edit-prompt="handleCancelEditPrompt"
              @submit-edit-prompt="handleSubmitEditPrompt($event)"
              @suggestion-select="handleSuggestionSelect"
            />
          </transition-group>

          <searching-indicator
            v-if="showSearchingIndicator"
            class="searching-indicator"
          />
        </v-col>
      </v-row>
    </div>

    <typing-indicator
      v-if="showTypingIndicator"
      class="typing-indicator-overlay"
      :class="{'typing-indicator-overlay--clickable': canJumpToBottom}"
      @click.native="handleTypingIndicatorClick"
    />

    <v-btn
      v-else-if="showJumpToBottomButton"
      ref="scrollButton"
      icon
      x-small
      class="jump-to-bottom-button"
      aria-label="Scroll to latest message"
      @click="handleJumpToBottomClick"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      <v-icon size="18">
        mdi-arrow-down
      </v-icon>
    </v-btn>
  </div>
</template>

<script>
import ChatMessage from './ChatMessage.vue'
import TypingIndicator from './TypingIndicator.vue'
import SearchingIndicator from './SearchingIndicator.vue'
import assistantService from '@/ai/services/AssistantService'
import AiChatUserInputType from '@/ai/model/AiChatUserInputType'

export default {
  name: 'ChatHistory',

  components: {
    ChatMessage,
    TypingIndicator,
    SearchingIndicator
  },

  dataStore: {
    assistantStore: 'AssistantStore'
  },

  data () {
    return {
      jumpAnimationFrame  : null,
      isJumpingToBottom   : false,
      pendingSessionScroll: false,
      editingMessageIndex : -1,
      tooltipElement      : null
    }
  },

  computed: {
    data () {
      return this.assistantStore.Messages.Data || []
    },

    showSearchingIndicator () {
      return this.assistantStore.Streaming.Active && !this.assistantStore.Streaming.AnimationActive
    },

    showTypingIndicator () {
      return this.assistantStore.Streaming.AnimationActive
    },

    streamingActive () {
      return this.assistantStore.Streaming.Active || this.assistantStore.Streaming.AnimationActive
    },

    autoScroll () {
      return this.assistantStore.Messages.AutoScroll
    },

    selectedSessionId () {
      return this.assistantStore.SelectedSession?.Data?.sessionId || null
    },

    canJumpToBottom () {
      return (
        this.streamingActive &&
        !this.autoScroll &&
        !this.isJumpingToBottom
      )
    },

    showJumpToBottomButton () {
      return (
        !this.streamingActive &&
        !this.autoScroll &&
        !this.isJumpingToBottom &&
        this.data.length > 0
      )
    },

    lastUserMessageIndex () {
      for (let index = this.data.length - 1; index >= 0; index -= 1) {
        if (this.data[index]?.role === 'USER') {
          return index
        }
      }

      return -1
    },

    lastAiMessageIndex () {
      for (let index = this.data.length - 1; index >= 0; index -= 1) {
        if (this.data[index]?.role === 'AI') {
          return index
        }
      }

      return -1
    }
  },

  watch: {
    selectedSessionId: {
      immediate: true,
      handler () {
        if (this.jumpAnimationFrame) {
          cancelAnimationFrame(this.jumpAnimationFrame)
          this.jumpAnimationFrame = null
        }

        this.isJumpingToBottom = false
        this.pendingSessionScroll = true
        this.editingMessageIndex = -1
        this.assistantStore.Messages.AutoScroll = true
        this.scrollToBottom(false)
      }
    },

    data: {
      handler () {
        if (this.pendingSessionScroll && this.data.length > 0) {
          this.pendingSessionScroll = false
          this.assistantStore.Messages.AutoScroll = true
          this.scrollToBottom(false)
          return
        }

        if (this.editingMessageIndex >= this.data.length) {
          this.editingMessageIndex = -1
        }

        if (this.autoScroll && !this.isJumpingToBottom) {
          this.scrollToBottom(false)
        }
      },
      deep: true
    },

    streamingActive () {
      if (this.autoScroll && !this.isJumpingToBottom) {
        this.scrollToBottom(false)
      }
    },

    editingMessageIndex: {
      immediate: true,
      handler (value) {
        this.assistantStore.Editing.Active = value !== -1
      }
    }
  },

  mounted () {
    this.scrollToBottom(false)
  },

  beforeDestroy () {
    if (this.jumpAnimationFrame) {
      cancelAnimationFrame(this.jumpAnimationFrame)
      this.jumpAnimationFrame = null
    }
    this.assistantStore.Editing.Active = false
    this.removeTooltip()
  },

  methods: {
    messageKey (message, index) {
      return message.id || `${message.role}-${index}`
    },

    isStreamingMessage (message, index) {
      return (
        this.streamingActive &&
        index === this.data.length - 1 &&
        message.role === 'AI'
      )
    },

    handleTypingIndicatorClick () {
      if (!this.canJumpToBottom) {
        return
      }

      this.animateToBottom()
    },

    handleJumpToBottomClick () {
      this.removeTooltip()
      if (!this.showJumpToBottomButton) {
        return
      }

      this.animateToBottom()
    },

    animateToBottom (duration = 320) {
      const container = this.$refs.history

      if (!container) {
        return
      }

      if (this.jumpAnimationFrame) {
        cancelAnimationFrame(this.jumpAnimationFrame)
        this.jumpAnimationFrame = null
      }

      this.isJumpingToBottom = true
      this.assistantStore.Messages.AutoScroll = false

      const startTop = container.scrollTop
      let startTs = null

      const easeOutCubic = t => 1 - Math.pow(1 - t, 3)

      const step = (ts) => {
        if (startTs === null) {
          startTs = ts
        }

        const progress = Math.min((ts - startTs) / duration, 1)
        const eased = easeOutCubic(progress)
        const targetTop = Math.max(
          container.scrollHeight - container.clientHeight,
          0
        )

        container.scrollTop =
          startTop + (targetTop - startTop) * eased

        if (progress < 1) {
          this.jumpAnimationFrame = requestAnimationFrame(step)
          return
        }

        container.scrollTop = container.scrollHeight
        this.jumpAnimationFrame = null
        this.isJumpingToBottom = false
        this.assistantStore.Messages.AutoScroll = true
      }

      this.jumpAnimationFrame = requestAnimationFrame(step)
    },

    handleScroll () {
      if (this.isJumpingToBottom) {
        return
      }

      const container = this.$refs.history

      if (!container) {
        return
      }

      const distanceFromBottom =
        container.scrollHeight -
        container.scrollTop -
        container.clientHeight

      this.assistantStore.Messages.AutoScroll =
        distanceFromBottom < 80
    },

    scrollToBottom (smooth = false) {
      this.$nextTick(() => {
        const container = this.$refs.history

        if (!container) {
          return
        }

        container.scrollTo({
          top     : container.scrollHeight,
          behavior: smooth ? 'smooth' : 'auto'
        })
      })
    },

    handleContentRendered () {
      if (this.autoScroll && !this.isJumpingToBottom) {
        this.scrollToBottom(false)
      }
    },

    handleRerunMessage (assistantIndex) {
      if (this.streamingActive || this.assistantStore.Editing.Active) {
        return
      }
      const previousMessage = this.data[assistantIndex - 1]
      if (!previousMessage || previousMessage.role !== 'USER') {
        return
      }
      const text = String(previousMessage.content || '')
      this.assistantStore.Messages.AutoScroll = true
      assistantService.sendMessage(text)
      this.scrollToBottom(false)
    },

    handleSuggestionSelect (suggestion) {
      if (this.streamingActive || this.assistantStore.Editing.Active) {
        return
      }

      const text = String(suggestion?.content || suggestion?.displayContent || '').trim()

      if (!text) {
        return
      }

      this.assistantStore.Messages.AutoScroll = true
      assistantService.sendMessage(text)
      this.scrollToBottom(false)
    },

    handleEditPrompt (messageIndex) {
      if (this.streamingActive) {
        return
      }
      this.editingMessageIndex = messageIndex
    },

    handleCancelEditPrompt () {
      this.editingMessageIndex = -1
    },

    handleSubmitEditPrompt (payload) {
      if (this.streamingActive) {
        return
      }
      const text = String(payload?.content || '').trim()
      if (!text) {
        return
      }
      const lastUserIndex = this.lastUserMessageIndex
      if (lastUserIndex !== -1) {
        this.assistantStore.Messages.Data.splice(lastUserIndex)
      }
      this.editingMessageIndex = -1
      this.assistantStore.Messages.AutoScroll = true
      assistantService.sendMessage(text, AiChatUserInputType.EDIT_MESSAGE, payload?.message?.streamId)
      this.scrollToBottom(false)
    },

    showTooltip () {
      const button = this.$refs.scrollButton

      if (!button || !button.$el) {
        return
      }

      this.removeTooltip()

      const tooltip = document.createElement('div')
      tooltip.className = 'scroll-tooltip'
      tooltip.textContent = 'Scroll to bottom'

      const rect = button.$el.getBoundingClientRect()
      tooltip.style.cssText = `
        position: fixed;
        bottom: ${window.innerHeight - rect.top + 8}px;
        left: ${rect.left + rect.width / 2}px;
        transform: translateX(-50%);
        padding: 3px 8px;
        background: white;
        color: #041f30;
        font-family: 'Inter';
        font-size: 11px;
        font-weight: 400;
        line-height: 21px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        white-space: nowrap;
        z-index: 99999;
        pointer-events: none;
        opacity: 0;
        animation: tooltipFadeInUp 0.15s ease forwards;
      `

      document.body.appendChild(tooltip)
      this.tooltipElement = tooltip
    },

    hideTooltip () {
      this.removeTooltip()
    },

    removeTooltip () {
      if (this.tooltipElement) {
        this.tooltipElement.remove()
        this.tooltipElement = null
      }
    }
  }
}
</script>

<style scoped>
.chat-history-shell {
  position: relative;

  display: flex;
  flex: 1 1 0;
  flex-direction: column;

  width: 100%;
  height: 100%;

  min-width: 0;
  min-height: 0;

  overflow: hidden;
}

.chat-history {
  flex: 1 1 0;

  width: 100%;
  height: 100%;

  min-width: 0;
  min-height: 0;

  padding: 6px 16px 26px;

  background: transparent;

  overflow-x: hidden;
  overflow-y: auto;

  box-sizing: border-box;
}

.messages-container {
  width: 100%;
  margin: 0;
}

.messages-col {
  width: 100%;
  min-width: 0;
  padding: 0;
}

.messages-list {
  display: flex;
  flex-direction: column;

  width: 100%;
  min-width: 0;

  gap: 14px;
}

.searching-indicator {
  margin-top: 6px;
}

.typing-indicator-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  z-index: 3;
  pointer-events: none;
}

.typing-indicator-overlay--clickable {
  pointer-events: auto;
  cursor: pointer;
}

.jump-to-bottom-button {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  z-index: 3;
  width: 28px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 50%;
  border: 1px solid rgba(143, 159, 193, 0.55);
  background: #F4F7FF !important;
  color: #63747D !important;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(6px);
}

.jump-to-bottom-button:hover {
  background: #FFFFFF !important;
  color: #041F30 !important;
}

.chat-history::-webkit-scrollbar {
  width: 6px;
}

.chat-history::-webkit-scrollbar-thumb {
  background: #cdd6e4;
  border-radius: 999px;
}

.chat-history::-webkit-scrollbar-track {
  background: transparent;
}

.message-in-enter-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.message-in-enter {
  opacity: 0;
  transform: translateY(6px);
}
</style>

<style>
@keyframes tooltipFadeInUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
